import { useState, type JSX } from 'react';
import { ArrowRight, ChevronDown, ChevronRight, TriangleAlert } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { CountryItem, ParamChange, ParamSummaryEntry } from '../api/types';
import { groupSummary, runsWithLine } from './model';
import { SoundList } from '../components/SoundLink';
import { soundRefsFromSetting } from '../lib/sounds';

/**
 * What a scheduled search runs with, in words. The wording comes from the
 * discovery server (built from the search schema), so this only lays it out:
 * a one-line "Runs with" for a table row, the full grouped panel, and a
 * before → after list for an edit.
 *
 * Country lists get their own treatment because a group id hides a lot: "EU"
 * is 27 countries, which is how a creator from one of them turns up in a
 * search someone thought was US/UK.
 */

function CountryChip({ item }: { item: CountryItem }): JSX.Element {
  const [open, setOpen] = useState(false);
  if (item.kind === 'country') {
    return (
      <span className="inline-flex items-center rounded-md border border-border bg-muted/40 px-1.5 py-0.5 text-xs" title={item.code}>
        {item.name}
      </span>
    );
  }
  if (item.kind === 'unknown') {
    return (
      <span
        className="inline-flex items-center rounded-md border border-warning/40 bg-warning/10 px-1.5 py-0.5 text-xs text-pending-strong"
        title="Not a country or a known group. The search ignores it."
      >
        {item.raw || 'Unknown'} (ignored)
      </span>
    );
  }
  const members = item.countries.map((c) => c.name).join(', ');
  return (
    <span className="inline-flex max-w-full flex-col">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        title={members}
        className="inline-flex items-center gap-1 rounded-md border border-brand/30 bg-brand/10 px-1.5 py-0.5 text-left text-xs hover:bg-brand/15"
      >
        {open ? <ChevronDown className="h-3 w-3 shrink-0" /> : <ChevronRight className="h-3 w-3 shrink-0" />}
        {item.name}
        <span className="text-muted-foreground">
          ({item.count} {item.count === 1 ? 'country' : 'countries'})
        </span>
      </button>
      {open && <span className="mt-1 whitespace-normal text-xs leading-relaxed text-muted-foreground">{members}</span>}
    </span>
  );
}

/** A setting's text, with the sounds of a sound search as links. */
function SettingText({ settingKey, label, value, max = 3 }: { settingKey: string; label: string; value: string; max?: number }): JSX.Element {
  const sounds = soundRefsFromSetting(settingKey, label, value);
  return sounds ? <SoundList sounds={sounds} max={max} /> : <>{value}</>;
}

function EntryValue({ entry }: { entry: ParamSummaryEntry }): JSX.Element {
  if (entry.items && entry.items.length > 0) {
    return (
      <span className="flex flex-wrap gap-1">
        {entry.items.map((item, i) => (
          <CountryChip key={`${item.kind}-${i}`} item={item} />
        ))}
      </span>
    );
  }
  return (
    <span className="whitespace-normal break-words">
      {entry.value ? <SettingText settingKey={entry.key} label={entry.label} value={entry.value} max={20} /> : '—'}
    </span>
  );
}

/** One line for a table row: countries first, then the main filters, then "+N more". */
export function RunsWithLine({
  summary,
  onShowAll,
}: {
  summary: ParamSummaryEntry[];
  onShowAll?: () => void;
}): JSX.Element | null {
  if (summary.length === 0) return null;
  const { shown, more } = runsWithLine(summary, 3);
  return (
    <p className="mt-0.5 whitespace-normal pl-[22px] text-xs leading-relaxed text-muted-foreground" data-runs-with>
      <span className="font-medium text-foreground/80">Runs with: </span>
      {shown.map((e, i) => (
        <span key={e.key}>
          {i > 0 && ' · '}
          {e.label}:{' '}
          <span className="text-foreground/85">
            <SettingText settingKey={e.key} label={e.label} value={e.value} />
          </span>
        </span>
      ))}
      {more > 0 &&
        (onShowAll ? (
          <>
            {' · '}
            <button type="button" onClick={onShowAll} className="font-medium text-brand hover:underline">
              +{more} more
            </button>
          </>
        ) : (
          ` · +${more} more`
        ))}
    </p>
  );
}

/** Every setting, grouped like the search form. */
export function SettingsPanel({
  summary,
  className,
}: {
  summary: ParamSummaryEntry[];
  className?: string;
}): JSX.Element | null {
  if (summary.length === 0) return null;
  return (
    <div className={cn('space-y-3', className)} data-settings-panel>
      {groupSummary(summary).map((g) => (
        <section key={g.label} aria-label={g.label}>
          <h4 className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{g.label}</h4>
          <dl className="mt-1 grid grid-cols-[minmax(7rem,11rem)_minmax(0,1fr)] gap-x-3 gap-y-1.5 text-sm">
            {g.entries.map((e) => (
              <div key={e.key} className="contents" data-setting={e.key}>
                <dt className={cn('text-muted-foreground', e.inactive && 'line-through')}>{e.label}</dt>
                <dd className={cn('min-w-0', e.inactive && 'text-muted-foreground')}>
                  <EntryValue entry={e} />
                  {e.note && <span className="mt-0.5 block text-xs text-muted-foreground">{e.note}</span>}
                </dd>
              </div>
            ))}
          </dl>
        </section>
      ))}
    </div>
  );
}

/** A collapsed "All settings" disclosure around the panel. */
export function SettingsDisclosure({
  summary,
  defaultOpen = false,
  label = 'All settings',
}: {
  summary: ParamSummaryEntry[];
  defaultOpen?: boolean;
  label?: string;
}): JSX.Element | null {
  const [open, setOpen] = useState(defaultOpen);
  if (summary.length === 0) return null;
  return (
    <div className="rounded-lg border border-border">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full items-center gap-1.5 px-3 py-2 text-left text-sm font-medium hover:bg-accent/40"
      >
        {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        {label}
        <span className="font-normal text-muted-foreground">({summary.length})</span>
      </button>
      {open && <SettingsPanel summary={summary} className="border-t border-border px-3 py-3" />}
    </div>
  );
}

/** Before → after for each changed setting; countries as added / removed names. */
export function ChangesList({ changes }: { changes: ParamChange[] }): JSX.Element {
  if (changes.length === 0) return <p className="text-sm text-muted-foreground">Nothing changes.</p>;
  return (
    <ul className="space-y-2 text-sm" data-settings-changes>
      {changes.map((c) => (
        <li key={c.key} className="rounded-lg border border-border px-3 py-2" data-change={c.key}>
          <p className="font-medium">{c.label}</p>
          {c.countriesAdded?.length || c.countriesRemoved?.length ? (
            <div className="mt-1 space-y-1 text-xs">
              {c.countriesRemoved && c.countriesRemoved.length > 0 && (
                <p className="whitespace-normal text-destructive-strong">
                  <span className="font-semibold">Removed ({c.countriesRemoved.length}):</span>{' '}
                  {c.countriesRemoved.map((x) => x.name).join(', ')}
                </p>
              )}
              {c.countriesAdded && c.countriesAdded.length > 0 && (
                <p className="whitespace-normal text-success-strong">
                  <span className="font-semibold">Added ({c.countriesAdded.length}):</span>{' '}
                  {c.countriesAdded.map((x) => x.name).join(', ')}
                </p>
              )}
            </div>
          ) : (
            <p className="mt-0.5 flex flex-wrap items-center gap-1.5 whitespace-normal text-xs text-muted-foreground">
              <span className="line-through">
                {c.before ? <SettingText settingKey={c.key} label={c.label} value={c.before} max={20} /> : 'Not set'}
              </span>
              <ArrowRight className="h-3 w-3 shrink-0" />
              <span className="text-foreground">
                {c.after ? <SettingText settingKey={c.key} label={c.label} value={c.after} max={20} /> : 'Not set'}
              </span>
            </p>
          )}
        </li>
      ))}
    </ul>
  );
}

/** Red count of added creators outside the countries a run filtered on. */
export function OutsideTargetBadge({ count, className }: { count: number | null; className?: string }): JSX.Element | null {
  if (!count || count <= 0) return null;
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-md border border-destructive/40 bg-destructive/10 px-1.5 py-0.5 text-xs font-semibold text-destructive-strong',
        className,
      )}
      title="Creators added from outside the countries the run filtered on. This should be zero."
      data-outside-target={count}
    >
      <TriangleAlert className="h-3 w-3" />
      {count.toLocaleString()} outside target
    </span>
  );
}
